import { makeAutoObservable, runInAction } from 'mobx';
import { SubscriberMultiLoginModule } from 'store/modules/subscriber/subscriber-multi-login/subscriberMultiLoginModule';
import { getRootStoreInstance } from 'store/rootStore';
import { SubscriberEntryNavigationModule } from 'store/modules/subscriber/subscriber-tasks/subscriberEntryNavigationModule';
import { createContext, useContext } from 'react';
import { RouteV2Id } from 'routes/routesConfigV2';
import { SupportedUserRoles } from '@insurancemenu/imenu-components-lib/dist/types';

export class EmployeeProfilePageStore {
  private _rootStore = getRootStoreInstance();

  public multiLoginModule = new SubscriberMultiLoginModule(this._rootStore);
  public subscriberEntryNavigationModule: SubscriberEntryNavigationModule | null = null;

  private _isGroupsListLoading = true;

  public get isGroupsListLoading(): boolean {
    return this._isGroupsListLoading;
  }

  public get isInitialLoading(): boolean {
    return this._isGroupsListLoading;
  }

  public setGroupsListLoading(isLoading: boolean): void {
    this._isGroupsListLoading = isLoading;
  }

  public async setupActiveGroup(groupIdFromUrl?: string): Promise<void> {
    this.setGroupsListLoading(true);
    await this.multiLoginModule.getGroupsList();
    await runInAction(async () => {
      await this.multiLoginModule.findActiveGroupAndRedirect(groupIdFromUrl);
      this.setGroupsListLoading(false);
    });
  }

  public buildSubscriberEntryNavigationModule(activeGroupId: string): void {
    if (!this.multiLoginModule.setActiveGroupById(activeGroupId)) {
      throw new Error(`Group with id ${activeGroupId} not found`);
    }
    this.subscriberEntryNavigationModule = new SubscriberEntryNavigationModule({ groupId: activeGroupId });
  }

  public async reLoginIntoActiveGroup(activeGroupId: string, subscriberRoleInGroup: SupportedUserRoles): Promise<void> {
    this.setGroupsListLoading(true);
    const routeToRedirect =
      subscriberRoleInGroup === 'EMPLOYEE' ? RouteV2Id.EMPLOYEE_PROFILE : RouteV2Id.EMPLOYER_PROFILE;
    await this.multiLoginModule.reLoginIntoActiveGroup(activeGroupId, routeToRedirect);
    runInAction(() => {
      this.setGroupsListLoading(false);
    });
  }

  constructor() {
    makeAutoObservable<EmployeeProfilePageStore, '_rootStore'>(
      this,
      {
        _rootStore: false,
        multiLoginModule: false,
      },
      { autoBind: true },
    );
  }
}

export const EmployeeProfilePageStoreContext = createContext<EmployeeProfilePageStore | null>(null);

export const useEmployeeProfilePageStore = (): EmployeeProfilePageStore => {
  const context = useContext(EmployeeProfilePageStoreContext);
  if (!context) {
    throw new Error('useEmployeeProfilePageStore must be used within a EmployeeProfilePageStoreContext');
  }
  return context;
};
